import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface Color {
  name: string;
  hex: string;
  stock: number;
}

interface ColorManagementProps {
  colors: Color[];
  onChange: (colors: Color[]) => void;
}

export const ColorManagement = ({ colors, onChange }: ColorManagementProps) => {
  const [newColor, setNewColor] = useState<Color>({ name: '', hex: '#000000', stock: 0 });

  const addColor = () => {
    if (!newColor.name || newColor.stock < 0) return;
    
    onChange([...colors, newColor]);
    setNewColor({ name: '', hex: '#000000', stock: 0 });
  };

  const removeColor = (index: number) => {
    onChange(colors.filter((_, i) => i !== index));
  };

  const predefinedColors = [
    { name: 'Noir', hex: '#000000' },
    { name: 'Blanc', hex: '#FFFFFF' },
    { name: 'Rouge', hex: '#FF0000' },
    { name: 'Bleu', hex: '#0066CC' },
    { name: 'Vert', hex: '#00CC00' },
    { name: 'Jaune', hex: '#FFCC00' },
    { name: 'Rose', hex: '#FF99CC' },
    { name: 'Violet', hex: '#9933FF' },
    { name: 'Orange', hex: '#FF9900' },
    { name: 'Gris', hex: '#999999' },
    { name: 'Marron', hex: '#996633' },
    { name: 'Beige', hex: '#F5DEB3' },
  ];

  return (
    <div className="space-y-4">
      <Label>Couleurs disponibles</Label>
      
      {/* Liste des couleurs ajoutées */}
      <div className="flex flex-wrap gap-2">
        {colors.map((color, index) => (
          <Badge 
            key={index} 
            variant="outline" 
            className="px-3 py-2 flex items-center gap-2"
          >
            <div 
              className="w-5 h-5 rounded-full border border-gray-300" 
              style={{ backgroundColor: color.hex }}
            />
            <span>{color.name}</span>
            <span className="text-xs text-muted-foreground">({color.stock} unités)</span>
            <button
              type="button"
              onClick={() => removeColor(index)}
              className="ml-1 hover:text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {colors.length === 0 && (
          <p className="text-sm text-muted-foreground">Aucune couleur ajoutée</p>
        )}
      </div>

      {/* Formulaire d'ajout */}
      <div className="border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="colorName" className="text-xs">Nom de la couleur</Label>
            <Input
              id="colorName"
              placeholder="Ex: Noir mat"
              value={newColor.name}
              onChange={(e) => setNewColor({ ...newColor, name: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="colorHex" className="text-xs">Code couleur</Label>
            <div className="flex gap-2">
              <Input
                id="colorHex"
                type="color"
                value={newColor.hex}
                onChange={(e) => setNewColor({ ...newColor, hex: e.target.value })}
                className="w-16 h-10 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={newColor.hex}
                onChange={(e) => setNewColor({ ...newColor, hex: e.target.value })}
                placeholder="#000000"
                className="flex-1"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="colorStock" className="text-xs">Stock</Label>
            <Input
              id="colorStock"
              type="number"
              min="0"
              value={newColor.stock}
              onChange={(e) => setNewColor({ ...newColor, stock: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>
        
        {/* Couleurs prédéfinies */}
        <div className="space-y-2">
          <Label className="text-xs">Couleurs rapides :</Label>
          <div className="flex flex-wrap gap-2">
            {predefinedColors.map((color) => (
              <button
                key={color.hex}
                type="button"
                onClick={() => setNewColor({ ...newColor, name: color.name, hex: color.hex })}
                className="flex items-center gap-1 px-2 py-1 rounded border hover:bg-accent text-xs"
              >
                <div 
                  className="w-4 h-4 rounded-full border border-gray-300" 
                  style={{ backgroundColor: color.hex }}
                />
                {color.name}
              </button>
            ))}
          </div>
        </div>

        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          onClick={addColor}
          disabled={!newColor.name}
        >
          <Plus className="h-4 w-4 mr-1" />
          Ajouter cette couleur
        </Button>
      </div>
    </div>
  );
};
